
import { i18n } from "@/i18n";
import { showMessage } from "@/services/MessageService";
import { toBase64 } from "@/services/FileToBase64";
import { loaderStart, loaderDone } from "@/services/Loader";
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  reactive,
  onUnmounted,
} from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";

export default defineComponent({
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const registerForm = ref<any>(null);
    const eventType = ref("");
    const ticketsQuantity = ref(1);
    const customFields = reactive({});
    const isQuotaAvailable = computed(
      () => store.getters["event/isQuotaAvailable"]
    );
    const participantEventData = reactive({
      ticketType: "",
      status: "pending",
    });

    const eventData = computed(() => store.getters["user/eventData"]);
    const ticketLabelFilter = (ticketType) => {
      if (ticketType)
        return `${ticketType.name} -- $ ${ticketType.price} ${ticketType.currency}`;
      return "";
    };

    const validCustomFields = computed(() => {
      let validatedFields: boolean[] = [];
      if (eventData.value.registrationFormTemplate) {
        eventData.value.registrationFormTemplate.forEach((field) => {
          if (field.required && field.value) {
            validatedFields.push(true);
          } else if (!field.required) {
            validatedFields.push(true);
          } else {
            validatedFields.push(false);
          }
        });
      }
      const validFields = validatedFields.indexOf(false);
      if (validFields === -1) {
        return true;
      } else {
        return false;
      }
    });
    const selectChange = async (file, index) => {
      try {
        if (file.target.files[0]) {
          const imageDataURL: any = await toBase64(file.target.files[0]);
          eventData.value.registrationFormTemplate[index].value = imageDataURL;
          eventData.value.registrationFormTemplate[index].valueName =
            file.target.files[0].name;
        }
      } catch {
        showMessage({
          title: "Error",
          message: "Error processing file",
          type: "error",
        });
      }
    };

    const send = async () => {
      const userData = {
        ...participantEventData,
        optionalFields: eventData.value.registrationFormTemplate
          ? eventData.value.registrationFormTemplate
          : null,
        registerDate: new Date(),
        createdDate: new Date(),
        status: "pending",
        eventName: eventData.value.name,
        eventID: eventData.value.id,
        ticketsQuantity: ticketsQuantity.value,
      };
      await registerForm.value.validate(async (valid) => {
        if (participantEventData.ticketType != "") {
          if (valid) {
            loaderStart();
            await store.dispatch("event/registerEventParticipant", userData);
            router.push({
              query: { ticketId: participantEventData.ticketType },
              name: "Checkout",
              params: { eventID: eventData.value.id },
            });
            loaderDone();
          } else {
            showMessage({
              title: i18n.global.t("register.formComplete.title"),
              message: i18n.global.t("register.formComplete.message"),
              type: "error",
            });
          }
        } else {
          showMessage({
            title: i18n.global.t("register.ticketSelect.title"),
            message: i18n.global.t("register.ticketSelect.message"),
            type: "error",
          });
        }
      });
    };
    const isValidTicket = (limitRegistationDate) => {
      const limitDate = new Date(limitRegistationDate.seconds * 1000);
      limitDate.setDate(limitDate.getDate() + 1);
      const today = new Date();
      return limitDate > today;
    };
    onMounted(async () => {
      loaderStart();
      await store.dispatch("event/getQuotaEvent", route.params.eventID);
      eventType.value = eventData?.value.type;
      if (eventData.value && eventData.value.registrationFormTemplate) {
        const registerTemplate = eventData.value.registrationFormTemplate;
        const fields = registerTemplate.reduce(
          (obj, item) => Object.assign(obj, { [item.id]: item }),
          {}
        );
        for (const key in fields) {
          if (Object.prototype.hasOwnProperty.call(fields, key)) {
            const field = fields[key];
            customFields[key] = field;
          }
        }
      }
      loaderDone();
    });
    onUnmounted(() => {
      for (const key in customFields) {
        if (Object.prototype.hasOwnProperty.call(customFields, key)) {
          if (customFields[key].value) customFields[key].value = null;
        }
      }
    });
    return {
      validCustomFields,
      participantEventData,
      eventData,
      selectChange,
      send,
      ticketLabelFilter,
      isQuotaAvailable,
      isValidTicket,
      customFields,
      registerForm,
      ticketsQuantity,
      eventType,
    };
  },
});
